import { createContext, useContext, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useAuth } from "@/context/AuthContext";
import { TOURS } from "@/lib/tours";
import { toast } from "sonner";

const TourContext = createContext(null);
export const useTour = () => useContext(TourContext);

export function TourProvider({ children }) {
  const nav = useNavigate();
  const { setTourMode } = useAuth();
  const switchingRef = useRef(false);
  const { user } = useAuth();
  const [lang, setLang] = useState(() => (localStorage.getItem("pns_train_lang") || "es"));
  const toggleLang = useCallback(() => {
    setLang((cur) => { const nx = cur === "en" ? "es" : "en"; localStorage.setItem("pns_train_lang", nx); return nx; });
  }, []);

  const build = useCallback((tour, en, startIndex) => {
    const d = driver({
      showProgress: true,
      allowClose: true,
      overlayColor: "rgba(15, 23, 42, 0.6)",
      nextBtnText: en ? "Next →" : "Siguiente →",
      prevBtnText: en ? "← Back" : "← Atrás",
      doneBtnText: en ? "Finish" : "Finalizar",
      progressText: "{{current}} / {{total}}",
      steps: tour.steps.map((s) => ({
        element: s.target,
        popover: {
          title: en ? s.title_en : s.title_es,
          description: en ? s.body_en : s.body_es,
        },
      })),
      onPopoverRender: (popover) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.textContent = en ? "ES" : "EN";
        btn.setAttribute("data-testid", "tour-lang-toggle");
        btn.title = en ? "Cambiar a español" : "Switch to English";
        btn.style.cssText =
          "margin-right:auto;font-size:11px;font-weight:800;border:1px solid #cbd5e1;border-radius:6px;padding:3px 9px;cursor:pointer;background:#fff;color:#2495D3;";
        btn.addEventListener("click", () => {
          const idx = d.getActiveIndex() ?? 0;
          const newEn = !en;
          localStorage.setItem("pns_train_lang", newEn ? "en" : "es");
          setLang(newEn ? "en" : "es");
          switchingRef.current = true;
          d.destroy();
          switchingRef.current = false;
          build(tour, newEn, idx);
        });
        popover.footerButtons.prepend(btn);
      },
      onDestroyed: () => { if (!switchingRef.current) setTourMode(false); },
    });
    d.drive(startIndex || 0);
  }, [setTourMode]);

  const startTour = useCallback((tourId) => {
    const tour = TOURS.find((t) => t.id === tourId);
    if (!tour) return;
    const en = lang === "en";
    setTourMode(true);
    nav(tour.route);
    setTimeout(() => build(tour, en, 0), 800);
  }, [nav, setTourMode, build, lang]);

  // Start the tour that matches the current route; fall back to the general "overview" tour.
  // Adding/editing steps in src/lib/tours.js automatically keeps every page's tour up to date.
  const startTourForRoute = useCallback((pathname) => {
    const matches = TOURS.filter((x) =>
      (x.route === pathname || (pathname !== "/" && x.route !== "/" && pathname.startsWith(x.route)))
      && (!x.roles || !user || x.roles.includes(user.role)));
    const t = matches.find((x) => x.id !== "overview") || matches[0];
    if (t) { startTour(t.id); return; }
    toast.info(lang === "en" ? "No guided tour for this section yet." : "Aún no hay recorrido guiado para esta sección.");
  }, [startTour, user, lang]);

  // Onboarding: auto-run a module's tour the FIRST time an employee (staff) opens it, once per user.
  const autoStartForRoute = useCallback((pathname) => {
    if (!user || user.role !== "staff") return;              // employees only
    const matches = TOURS.filter((x) =>
      (x.route === pathname || (pathname !== "/" && x.route !== "/" && pathname.startsWith(x.route)))
      && (!x.roles || x.roles.includes(user.role)));
    if (!matches.length) return;
    const tour = matches.find((x) => x.id !== "overview") || matches[0];   // prefer a page-specific tour
    const first = tour.steps && tour.steps[0] && tour.steps[0].target;
    if (first && !document.querySelector(first)) return;     // page not ready / not visible for this role — retry later (don't burn the "seen" flag)
    const key = `pns_tour_seen_${user.email || "anon"}`;
    let seen = [];
    try { seen = JSON.parse(localStorage.getItem(key) || "[]"); } catch (e) { seen = []; }
    if (seen.includes(tour.id)) return;
    seen.push(tour.id);
    localStorage.setItem(key, JSON.stringify(seen));         // mark seen only once actually shown, so it never re-pops
    startTour(tour.id);
  }, [user, startTour]);

  return <TourContext.Provider value={{ startTour, startTourForRoute, autoStartForRoute, lang, toggleLang }}>{children}</TourContext.Provider>;
}
